import { useEffect, useState } from "react";

import { premiseCopy } from "../content/productCopy";
import { colorPalette, type ColorId } from "../design/productConfig";
import {
  canonicalSharePathFromLocation,
  shareIdFromLocation,
} from "../features/sharing/shareRoute";
import { analytics } from "../services/analytics/analytics";
import { errors } from "../services/errors/errors";
import { NetlifyShareReportApi } from "../services/share-api/NetlifyShareReportApi";
import type { PublicShareReport } from "../services/share-api/ShareReportApi";
import { useTrafficStore } from "../stores/useTrafficStore";
import { LocationLabel } from "./LocationLabel";
import { VehicleArt } from "./VehicleArt";

const shareReportApi = new NetlifyShareReportApi();

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export function EntryPanel() {
  const phase = useTrafficStore((state) => state.phase);
  const profile = useTrafficStore((state) => state.profile);
  const returningDriver = useTrafficStore((state) => state.returningDriver);
  const driverCount = useTrafficStore((state) => state.driverCount);
  const transportMode = useTrafficStore((state) => state.transportMode);
  const sessionId = useTrafficStore((state) => state.sessionId);
  const merge = useTrafficStore((state) => state.merge);
  const mergeError = useTrafficStore((state) => state.mergeError);
  const viewerLocation = useTrafficStore((state) => state.viewerLocation);
  const shareId = shareIdFromLocation(location.pathname, location.search);
  const [sharedReport, setSharedReport] = useState<PublicShareReport | null>(
    null,
  );
  const [shareLoadFailed, setShareLoadFailed] = useState(false);
  const visible = phase === "spectating" || phase === "merging";
  const countAvailable = transportMode === "mock" || sessionId !== null;

  useEffect(() => {
    const canonicalPath = canonicalSharePathFromLocation(
      location.pathname,
      location.search,
    );
    if (canonicalPath) {
      history.replaceState(history.state, "", canonicalPath);
    }
    if (!shareId || transportMode !== "supabase") return;
    let active = true;
    void shareReportApi
      .get(shareId)
      .then((report) => {
        if (!active) return;
        setSharedReport(report);
        analytics.track("referral_opened", { source: "share_report" });
      })
      .catch((error: unknown) => {
        if (active) setShareLoadFailed(true);
        errors.capture(error, { action: "load_share_referral" });
      });
    return () => {
      active = false;
    };
  }, [shareId, transportMode]);

  if (!visible) return null;

  if (shareId) {
    const reportColor = sharedReport?.profile.colorId as ColorId | undefined;
    const reportColorHex =
      reportColor && reportColor in colorPalette
        ? `#${colorPalette[reportColor].toString(16).padStart(6, "0")}`
        : "#ff5a1f";
    return (
      <section
        className="entry-panel entry-panel--shared"
        aria-labelledby="shared-report-title"
      >
        <p className="eyebrow">SHARED TRAFFIC REPORT</p>
        {sharedReport ? (
          <>
            <div className="shared-report__hero">
              <div className="shared-report__vehicle">
                <VehicleArt
                  preset={sharedReport.profile.vehiclePreset}
                  color={reportColorHex}
                />
                <span>{sharedReport.profile.plate}</span>
              </div>
              <div>
                <h1 id="shared-report-title">
                  {sharedReport.profile.plate} sent you traffic.
                </h1>
                <p className="shared-report__state">
                  {sharedReport.worstState} ·{" "}
                  {sharedReport.peakDriverCount.toLocaleString()} peak drivers
                </p>
              </div>
            </div>
            <dl className="shared-report__metrics">
              <div>
                <dt>Time in traffic</dt>
                <dd>{formatDuration(sharedReport.elapsedSeconds)}</dd>
              </div>
              <div>
                <dt>Distance</dt>
                <dd>{sharedReport.distanceMiles.toFixed(2)} mi</dd>
              </div>
              <div>
                <dt>Delay endured</dt>
                <dd>{sharedReport.enduredDelaySeconds.toFixed(1)} sec</dd>
              </div>
              <div>
                <dt>Delay added</dt>
                <dd>{sharedReport.addedDelaySeconds.toFixed(1)} sec</dd>
              </div>
            </dl>
          </>
        ) : (
          <>
            <h1 id="shared-report-title">
              {shareLoadFailed ? "Report missed its exit." : "Loading report…"}
            </h1>
            <p className="entry-panel__premise">
              {shareLoadFailed
                ? "The shared report is unavailable, but the traffic jam is still here."
                : "Retrieving the damage from the Department of Internet Congestion."}
            </p>
          </>
        )}
        <p className="entry-panel__count">
          <span className="live-dot" aria-hidden="true" />
          {countAvailable
            ? `${driverCount.toLocaleString()} ${
                driverCount === 1 ? "driver" : "drivers"
              } currently making this worse`
            : "Live count connects when you merge."}
        </p>
        <button
          className="button button--primary button--merge"
          type="button"
          disabled={phase === "merging"}
          onClick={() => void merge()}
        >
          <span>{phase === "merging" ? "Finding a gap…" : "Join the jam"}</span>
          <span aria-hidden="true">→</span>
        </button>
        {mergeError ? (
          <p className="entry-panel__note" role="alert">
            {mergeError}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section className="entry-panel" aria-labelledby="entry-title">
      <p className="eyebrow">{premiseCopy.eyebrow}</p>
      <h1 id="entry-title">{premiseCopy.title}</h1>
      <p className="entry-panel__premise" aria-live="polite">
        {premiseCopy.body}
      </p>
      <p className="entry-panel__count">
        <span className="live-dot" aria-hidden="true" />
        {countAvailable
          ? `${driverCount.toLocaleString()} ${
              driverCount === 1 ? "driver" : "drivers"
            } currently making this worse`
          : transportMode === "supabase"
            ? "Live count connects when you merge."
            : "Radio offline. Running the local road."}
      </p>
      {returningDriver && profile ? (
        <p className="entry-panel__returning">
          <span className="entry-panel__returning-line">
            Welcome back, <strong>{profile.plate}</strong>
            <span>{profile.odometerMiles.toFixed(1)} lifetime miles</span>
          </span>
          <LocationLabel
            location={viewerLocation}
            className="entry-panel__location"
          />
        </p>
      ) : null}
      <button
        className="button button--primary button--merge"
        type="button"
        disabled={phase === "merging"}
        onClick={() => void merge()}
      >
        <span>
          {phase === "merging"
            ? "Finding a gap…"
            : returningDriver
              ? premiseCopy.mergeAgain
              : premiseCopy.merge}
        </span>
        <span aria-hidden="true">→</span>
      </button>
      {mergeError ? (
        <p className="entry-panel__note" role="alert">
          {mergeError}
        </p>
      ) : (
        <p className="entry-panel__note">
          No account. No destination. Everyone shares the speed.
        </p>
      )}
    </section>
  );
}
